// Iter56 — LockActionPanel
//
// Modal panel that handles both lock execution (warehouse worker records
// locked items) and release. The mode is inferred from `lock.status`:
//   - requested → render Execute Lock form with locked_items[] editor
//   - locked    → render Release form with release_note
import React, { useState } from "react";
import axios from "axios";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCK_TYPES = [
  { value: "quarantine", label: "Quarantine", color: "#f3b14a" },
  { value: "blocked", label: "Blocked", color: "#E24B4A" },
  { value: "hold", label: "Hold", color: "#9ec3ec" },
];

const emptyItem = () => ({ location: "", quantity: "", lock_type: "quarantine" });

export const LockActionPanel = ({ lock, onClose, onDone }) => {
  const addToast = useStore((s) => s.addToast);
  const mode = lock.status === "requested" ? "lock" : "release";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Lock mode state
  const [items, setItems] = useState([emptyItem()]);
  const [note, setNote] = useState("");

  // Release mode state
  const [releaseNote, setReleaseNote] = useState("");

  const update = (idx, patch) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addRow = () => setItems((prev) => [...prev, emptyItem()]);
  const removeRow = (idx) => setItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const submitLock = async () => {
    setError("");
    const valid = items
      .map((it) => ({
        location: (it.location || "").trim(),
        quantity: parseInt(it.quantity || 0, 10) || 0,
        lock_type: it.lock_type,
      }))
      .filter((it) => it.location && it.quantity > 0);
    if (!valid.length) { setError("At least one locked item with location and quantity > 0 is required"); return; }
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/stock-locks/${lock.id}/lock`, {
        locked_items: valid,
        note: note.trim() || null,
      });
      addToast({ type: "success", title: "Stock lock executed", body: `${data.id} — ${valid.length} location(s)` });
      onDone?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submit failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const submitRelease = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/stock-locks/${lock.id}/release`, {
        release_note: releaseNote.trim() || null,
      });
      addToast({ type: "success", title: "Stock lock released", body: data.id });
      onDone?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submit failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-3"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
      onClick={onClose}
      data-testid="lock-action-overlay"
    >
      <div
        className="rounded-md p-4"
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.18)",
          width: "min(720px, 96vw)", maxHeight: "92vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lock-action-panel"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)" }}>
              {mode === "lock" ? "Execute Lock" : "Release Lock"}
            </span>
            <span className="ml-2" style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9ec3ec" }}>{lock.id}</span>
            <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", marginTop: 2 }}>
              Part {lock.part_number} — {lock.reason}
            </div>
          </div>
          <button onClick={onClose} style={{ color: "#9aa1b3", fontSize: 22, cursor: "pointer", background: "transparent", border: "none" }} data-testid="lock-action-close">×</button>
        </div>

        {mode === "lock" && (
          <>
            <div className="mb-2 flex items-center justify-between">
              <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: "1.2px", color: "#9198b0", textTransform: "uppercase" }}>
                Locked items — one row per location
              </span>
              <button
                type="button" onClick={addRow}
                className="px-2.5 py-1 rounded"
                style={{ background: "rgba(24,95,165,0.16)", border: "1px solid rgba(24,95,165,0.55)", color: "#9ec3ec", fontFamily: "DM Mono, monospace", fontSize: 10, cursor: "pointer" }}
                data-testid="lock-action-add-row"
              >
                ＋ Add location
              </button>
            </div>

            <div className="rounded-md overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="lock-action-items-table">
              {items.map((it, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.05)", background: "var(--bg-surface, #0d0f18)" }}
                  data-testid={`lock-action-item-row-${i}`}
                >
                  <div className="col-span-6">
                    <input
                      type="text" value={it.location} onChange={(e) => update(i, { location: e.target.value })}
                      className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()}
                      placeholder="Warehouse A / Rack 12"
                      data-testid={`lock-action-location-${i}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number" min="0" value={it.quantity} onChange={(e) => update(i, { quantity: e.target.value })}
                      className="w-full px-2 py-1.5 rounded outline-none" style={{ ...inputStyle(), textAlign: "right" }}
                      placeholder="Qty"
                      data-testid={`lock-action-quantity-${i}`}
                    />
                  </div>
                  <div className="col-span-3">
                    <select
                      value={it.lock_type} onChange={(e) => update(i, { lock_type: e.target.value })}
                      className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()}
                      data-testid={`lock-action-type-${i}`}
                    >
                      {LOCK_TYPES.map((lt) => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button" onClick={() => removeRow(i)} disabled={items.length <= 1}
                      style={{ color: items.length <= 1 ? "#3a3f55" : "#ff9a99", cursor: items.length <= 1 ? "not-allowed" : "pointer", fontSize: 14, background: "transparent", border: "none" }}
                      data-testid={`lock-action-remove-${i}`}
                    >✕</button>
                  </div>
                </div>
              ))}
            </div>

            <Field label="Note (optional)">
              <textarea
                value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                className="w-full px-2 py-1.5 rounded outline-none"
                style={{ ...inputStyle(), resize: "vertical" }}
                placeholder="Operator notes…"
                data-testid="lock-action-note"
              />
            </Field>
          </>
        )}

        {mode === "release" && (
          <>
            <div className="mb-3 p-3 rounded" style={{ background: "rgba(78,208,132,0.06)", border: "1px solid rgba(78,208,132,0.30)", fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9aebc2" }}>
              Currently locked: {(lock.locked_items || []).length} location(s),{" "}
              {(lock.locked_items || []).reduce((s, it) => s + (it.quantity || 0), 0).toLocaleString()} pcs.
              Once released, parts are returned to normal stock.
            </div>
            <Field label="Release note (recommended)">
              <textarea
                value={releaseNote} onChange={(e) => setReleaseNote(e.target.value)} rows={3}
                className="w-full px-2 py-1.5 rounded outline-none"
                style={{ ...inputStyle(), resize: "vertical" }}
                placeholder="e.g. Inspection complete — lot passed re-validation"
                data-testid="lock-action-release-note"
              />
            </Field>
          </>
        )}

        {error && (
          <div className="mt-2 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 11 }} data-testid="lock-action-error">
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="px-3 py-2 rounded-md"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="lock-action-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={mode === "lock" ? submitLock : submitRelease}
            disabled={busy}
            className="px-3 py-2 rounded-md"
            style={{
              background: mode === "lock" ? "#E24B4A" : "#4ed084",
              color: "#fff", fontFamily: "Syne, sans-serif",
              fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.6 : 1, border: "none",
            }}
            data-testid="lock-action-submit"
          >
            {busy ? "Submitting…" : (mode === "lock" ? "Execute lock" : "Release lock")}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="mt-3">
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

const inputStyle = (fontSize = 12) => ({
  background: "var(--bg3, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize,
});
